import { useEffect, useState } from 'react';
import { MapView } from '../map/MapView';
import { BottomSheet } from '../components/BottomSheet';
import { AddressSearch } from '../components/AddressSearch';
import { TariffList } from '../components/TariffList';
import { CitySelector } from '../components/CitySelector';
import { ServicesSheet } from '../components/ServicesSheet';
import { DriverCard } from '../components/DriverCard';
import { OffersList } from '../components/OffersList';
import { useOrderStore, getServicesPrice } from '../store/orderStore';
import { config } from '../api/config';
import { STAGE_LABEL } from '../domain/stage';
import type { RoutePoint } from '../domain/types';

// Mirrors flutter_client: at most 4 route points (1 pickup + up to 3 stops).
const MAX_POINTS = 4;

const POINT_LABELS = ['A', 'B', 'C', 'D'];

function priceText(value: number): string {
  return `${Math.round(value)} ${config.defaultCurrency}`;
}

export function OrderScreen() {
  const {
    stage,
    selectedCity,
    points,
    quote,
    selectedRate,
    order,
    loadingQuote,
    locationStatus,
    error,
    availablePreferences,
    selectedPreferences,
    driverRaw,
    driverRouteGeometry,
    followDriver,
    realtimeConnected,
    offers,
    acceptingOfferId,
    nearbyDrivers,
    selectCity,
    changeCity,
    setPoint,
    addPoint,
    removePoint,
    requestPickupLocation,
    selectRate,
    fetchQuote,
    confirmOrder,
    recoverActiveOrder,
    cancel,
    reset,
    loadPreferences,
    setSelectedPreferences,
    toggleFollowDriver,
    acceptOffer,
    dismissOffer,
    syncNearby,
  } = useOrderStore();

  const [showServices, setShowServices] = useState(false);

  // On mount, recover any active order so a refresh resumes tracking, and make
  // sure additional services are available for the TRAVEL_OPTIONS sheet.
  useEffect(() => {
    void recoverActiveOrder();
    void loadPreferences();
    return () => useOrderStore.getState().stopPolling();
  }, [recoverActiveOrder, loadPreferences]);

  // Nearby free cars (ether) follow the pickup and pre-trip stages — restart
  // the poll when the pickup moves or the stage changes.
  useEffect(() => {
    syncNearby();
  }, [stage, points[0], syncNearby]);

  const filled = points.filter((p): p is RoutePoint => p !== null);
  const filledCount = filled.length;
  const canQuote = filledCount >= 2 && !loadingQuote;
  const cityCenter = selectedCity ? { lat: selectedCity.lat, lon: selectedCity.lon } : null;
  const pickup = points[0] ?? null;

  const servicesPrice = getServicesPrice(availablePreferences, selectedPreferences);
  const finalPrice = (selectedRate?.price ?? 0) + servicesPrice;
  const currency = config.defaultCurrency;

  const isLiveStage =
    stage === 'SEARCH_DRIVER' ||
    stage === 'PLANNED_SEARCH_DRIVER' ||
    stage === 'DRIVER_ON_THE_WAY' ||
    stage === 'DRIVER_IS_WAITING' ||
    stage === 'TRIP_IN_PROGRESS' ||
    stage === 'AWAITING_PAYMENT';

  const lastSlotFilled = points[points.length - 1] != null;
  const canAddStop = points.length < MAX_POINTS && lastSlotFilled;

  return (
    <div className="order-screen">
      <MapView
        center={cityCenter}
        points={points}
        routeGeometry={quote?.route.geometry ?? null}
        driver={driverRaw}
        driverRouteGeometry={driverRouteGeometry}
        followDriver={followDriver}
        nearbyDrivers={nearbyDrivers}
      />

      {isLiveStage && driverRaw && (
        <button
          className={`follow-button${followDriver ? ' active' : ''}`}
          aria-pressed={followDriver}
          onClick={() => toggleFollowDriver()}
        >
          {followDriver ? 'Стежу' : 'Стежити за водієм'}
        </button>
      )}

      {!selectedCity && stage === 'NO_ORDER' && (
        <BottomSheet title="Оберіть місто">
          <p className="muted">
            Запамʼятаємо місто для наступних замовлень. Його можна змінити у будь-який момент.
          </p>
          <CitySelector selectedCity={selectedCity} onSelect={(city) => void selectCity(city)} />
        </BottomSheet>
      )}

      {(stage === 'NO_ORDER' || stage === 'BUILD_ROUTE') && selectedCity && (
        <BottomSheet title="Куди їдемо?">
          <div className="city-row">
            <span>Місто: <strong>{selectedCity.name}</strong></span>
            <button className="link-button compact" onClick={() => changeCity()}>
              Змінити
            </button>
          </div>

          {/* Pickup field — always visible. Geolocation is a helper to auto-fill
              it, not a gate that hides the field. */}
          <AddressSearch
            label={POINT_LABELS[0]}
            placeholder={
              locationStatus === 'denied'
                ? 'Геопозицію не дозволено — введіть адресу'
                : 'Звідки'
            }
            near={cityCenter}
            value={pickup}
            onPick={(p) => void setPoint(0, p)}
          />
          {!pickup && locationStatus !== 'denied' && locationStatus !== 'unsupported' && (
            <button
              className="link-button geo-helper"
              disabled={locationStatus === 'requesting'}
              onClick={() => requestPickupLocation()}
            >
              {locationStatus === 'requesting' ? 'Визначаємо…' : '📍 Використати мою геопозицію'}
            </button>
          )}

          <AddressSearch
            label={POINT_LABELS[1]}
            placeholder="Куди"
            near={cityCenter}
            value={points[1] ?? null}
            onPick={(p) => void setPoint(1, p)}
          />

          {points.slice(2).map((slot, i) => {
            const index = i + 2;
            return (
              <div className="stop-row" key={`stop-${index}`}>
                <AddressSearch
                  label={POINT_LABELS[index] ?? String(index + 1)}
                  placeholder="Зупинка"
                  near={cityCenter}
                  value={slot}
                  onPick={(p) => void setPoint(index, p)}
                />
                <button
                  className="stop-remove"
                  aria-label="Видалити зупинку"
                  onClick={() => removePoint(index)}
                >
                  ✕
                </button>
              </div>
            );
          })}

          {canAddStop && (
            <button className="link-button add-stop" onClick={() => addPoint()}>
              + Додати зупинку
            </button>
          )}

          {error && <p className="error-text">{error}</p>}
          <button className="primary-button" disabled={!canQuote} onClick={() => fetchQuote()}>
            {loadingQuote ? 'Розрахунок…' : 'Розрахувати вартість'}
          </button>
        </BottomSheet>
      )}

      {stage === 'TRAVEL_OPTIONS' && quote && (
        <BottomSheet title="Оберіть тариф">
          <TariffList rates={quote.rates} selected={selectedRate} onSelect={selectRate} />

          <button
            className={`services-button${selectedPreferences.length > 0 ? ' active' : ''}`}
            onClick={() => setShowServices(true)}
          >
            <span>Послуги</span>
            {selectedPreferences.length > 0 && (
              <strong className="services-button-total">+{servicesPrice} {currency}</strong>
            )}
          </button>

          <div className="order-total">
            <span>До сплати</span>
            <strong>{priceText(finalPrice)}</strong>
          </div>

          {error && <p className="error-text">{error}</p>}
          <div className="sheet-actions">
            <button className="link-button" onClick={() => reset()}>
              Змінити маршрут
            </button>
            <button
              className="primary-button"
              disabled={!selectedRate}
              onClick={() => confirmOrder()}
            >
              Замовити
            </button>
          </div>
        </BottomSheet>
      )}

      {isLiveStage && (
        <BottomSheet title={STAGE_LABEL[stage]}>
          <div className="status-block">
            {(stage === 'SEARCH_DRIVER' || stage === 'PLANNED_SEARCH_DRIVER') && (
              <div className="spinner" aria-label="Пошук" />
            )}
            {order?.driverId && stage !== 'SEARCH_DRIVER' && stage !== 'PLANNED_SEARCH_DRIVER' && (
              <p className="driver-line">
                <span className="car-dot" /> Водій призначений
                <span className={`realtime-dot${realtimeConnected ? ' on' : ''}`} title={realtimeConnected ? 'Реалтайм зʼєднано' : 'Реалтайм вимкнено'} />
              </p>
            )}
            {order && (
              <p className="muted">
                Орієнтовна вартість: {Math.round(order.priceEstimated)}
              </p>
            )}
            {error && <p className="error-text">{error}</p>}
          </div>

          {/* Incoming driver offers during search — accept one to assign the driver. */}
          {(stage === 'SEARCH_DRIVER' || stage === 'PLANNED_SEARCH_DRIVER') && offers.length > 0 && (
            <OffersList
              offers={offers}
              acceptingOfferId={acceptingOfferId}
              currency={currency}
              onAccept={(id) => void acceptOffer(id)}
              onDismiss={(id) => dismissOffer(id)}
            />
          )}

          {/* Assigned-driver card once a driver is assigned. Fields populate
              progressively from the order poll (photo/name/car may lag driverId
              by a tick). */}
          {order?.driverId &&
            (stage === 'DRIVER_ON_THE_WAY' ||
              stage === 'DRIVER_IS_WAITING' ||
              stage === 'TRIP_IN_PROGRESS') && <DriverCard order={order} />}

          {(stage === 'SEARCH_DRIVER' ||
            stage === 'PLANNED_SEARCH_DRIVER' ||
            stage === 'DRIVER_ON_THE_WAY' ||
            stage === 'DRIVER_IS_WAITING') && (
            <button className="danger-button" onClick={() => cancel()}>
              Скасувати замовлення
            </button>
          )}
        </BottomSheet>
      )}

      {stage === 'DRIVER_CANCELLED_ORDER' && (
        <BottomSheet title={STAGE_LABEL.DRIVER_CANCELLED_ORDER}>
          <button className="primary-button" onClick={() => reset()}>
            Нове замовлення
          </button>
        </BottomSheet>
      )}

      {showServices && (
        <ServicesSheet
          available={availablePreferences}
          selected={selectedPreferences}
          currency={currency}
          onApply={(ids) => {
            setSelectedPreferences(ids);
            setShowServices(false);
          }}
          onClose={() => setShowServices(false)}
        />
      )}
    </div>
  );
}
